import React from 'react';

const stateCaptureGroup =
  /\s(A[KLRZ]|C[AOT]|D[CE]|FL|GA|HI|I[ADLN]|K[SY]|LA|M[ADEINOST]|N[CDEHJMVY]|O[HKR]|P[AR]|RI|S[CD]|T[NX]|UT|V[AIT]|W[AIVY])[,\s]/;

const getTruncatedStringJSX = ({str, endingChars = 3}: {str: string; endingChars?: number}) => {
  if (str.length <= endingChars + 1) {
    return <span>{str}</span>;
  }
  const splitLocation = str.length - (endingChars + 1);
  const str1 = str.substring(0, splitLocation);
  const str2 = str.substring(splitLocation, str.length);
  // These are purely presentational
  const span1 = (
    <span aria-hidden='true' className='truncate'>
      {str1}
    </span>
  );
  const span2 = <span aria-hidden='true'>{str2}</span>;
  // Title will allow elements to be read as the full string
  return (
    <span title={str} className='flex overflow-hidden'>
      {span1}
      {span2}
    </span>
  );
};

function clamp(str: string, charLimit = str.length, truncateChar = ''): string {
  if (str.length > charLimit) {
    return str.slice(0, charLimit) + truncateChar;
  }
  return str;
}

function upperFirst(str: string): string {
  return str.charAt(0).toUpperCase().concat(str.slice(1));
}

function stripNonAlphaNumeric(str = ''): string {
  return str.replace(/[^a-zA-Z0-9]/g, '');
}

function stripNonAlphaNumericWithSpaces(str = ''): string {
  return str.replace(/[^\w\s]/gi, '');
}

function findStateAbbr(str: string): string | null {
  const match = stateCaptureGroup.exec(str);
  return match ? match[1] : null;
}

function pluralize(str: string, count: number | null | undefined, customPlural?: string): string {
  const pluralForm = customPlural || `${str}s`;
  return count === 1 ? str : pluralForm;
}

function normalizeSearchString(str = ''): string {
  return stripNonAlphaNumericWithSpaces(str).toLowerCase();
}

function toSlug(name: string | null | undefined): string {
  if (!name) {
    return '';
  }
  const sanitized = stripNonAlphaNumericWithSpaces(name).trim().toLowerCase();
  return sanitized.replace(/\s+/g, '-').replace(/-+/g, '-');
}

const arrayToReadableString = (arr: string[], conjunction = 'and'): string => {
  if (arr.length === 0) {
    return '';
  }

  if (arr.length === 1) {
    return arr[0];
  }
  return [arr.slice(0, -1).join(', '), arr[arr.length - 1]].join(` ${conjunction} `);
};

function camelCaseToReadableString(value: string, capitalize = false): string {
  const result = value
    .split(/(?=[A-Z])/)
    .map((string) => string.toLowerCase())
    .join(' ');
  // capitalize all words if needed
  return capitalize ? result.replace(/\b\w/g, (char) => char.toUpperCase()) : result;
}

function getNumberWithSuffix(attempt: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const value = attempt % 100;
  return attempt + (suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]);
}

function formatNumberWithCommas(num: number | null | undefined): string {
  if (num == null) {
    return '0';
  }
  return num.toLocaleString();
}

const getSimilarSubstring = (text1 = '', text2 = ''): string => {
  const text1Lower = text1.toLowerCase();
  const text2Lower = text2.toLowerCase();

  let longest = '';

  for (let start = 0; start < text2Lower.length; start += 1) {
    for (let end = start + 1; end <= text2Lower.length; end += 1) {
      const substring = text2Lower.slice(start, end);
      if (text1Lower.includes(substring) && substring.length > longest.length) {
        longest = substring;
      }
    }
  }

  return longest;
};

function sanitizeFilename(filename: string) {
  return filename.replace(/[^-\w]+/gi, '_');
}

/** Strips a lone surrogate at either end: half a pair cut by a code-unit slice; invalid JSON. */
function trimLoneSurrogates(text: string) {
  return text.replace(/^[\uDC00-\uDFFF]/, '').replace(/[\uD800-\uDBFF]$/, '');
}

export {
  arrayToReadableString,
  clamp,
  findStateAbbr,
  upperFirst,
  stripNonAlphaNumeric,
  stripNonAlphaNumericWithSpaces,
  pluralize,
  normalizeSearchString,
  toSlug,
  camelCaseToReadableString,
  getTruncatedStringJSX,
  getNumberWithSuffix,
  formatNumberWithCommas,
  getSimilarSubstring,
  sanitizeFilename,
  trimLoneSurrogates
};
